import { InfoIcon } from '@sharedCR/coreDesignSystem/icons';
import { compose } from 'redux';

import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import { getConditionalArray } from 'utils/data';
import { fillConfigFieldsWithKeys } from 'utils/loanManagement';

import { PropertyAndLoanTestIds } from '../../../../PropertyAndLoan.testIds';
import {
  customIconStyles,
  customIconWrapperStyles,
  customTitleStyles,
  customTitleWrapperStyles,
} from '../MainContent.styles';

const chattelPropertyExpensesConfigFields = {
  monthlyLotRent: {
    fieldId: 'monthlyLotRent',
    type: FieldType.INPUT,
    nameInModel: 'monthlyLotRent',
    inputProps: {
      type: FieldInputType.CURRENCY,
      decimalScale: 2,
      placeholder: 'Monthly Lot Rent',
      label: 'Monthly Lot Rent',
      disabled: false,
      required: false,
      minValue: 0,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_PROPERTY_EXPENSES_FORM_MONTHLY_LOT_RENT_INPUT,
    },
  },
  salesPrice: {
    fieldId: 'salesPrice',
    type: FieldType.INPUT,
    nameInModel: 'salesPrice',
    inputProps: {
      type: FieldInputType.CURRENCY,
      decimalScale: 2,
      placeholder: 'Sales Price',
      label: 'Sales Price',
      disabled: false,
      required: false,
      minValue: 0,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_PROPERTY_EXPENSES_FORM_SALES_PRICE_INPUT,
    },
  },
  landMonthlyPayment: {
    fieldId: 'landMonthlyPayment',
    type: FieldType.INPUT,
    nameInModel: 'landMonthlyPayment',
    inputProps: {
      type: FieldInputType.CURRENCY,
      decimalScale: 2,
      placeholder: 'Monthly Land Payment',
      label: 'Monthly Land Payment',
      disabled: false,
      required: false,
      minValue: 0,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_PROPERTY_EXPENSES_FORM_LAND_MONTHLY_PAYMENT_INPUT,
    },
  },
  salesTax: {
    fieldId: 'salesTax',
    type: FieldType.INPUT,
    nameInModel: 'salesTax',
    inputProps: {
      type: FieldInputType.CURRENCY,
      decimalScale: 2,
      placeholder: 'Sales Tax',
      label: 'Sales Tax',
      disabled: false,
      required: false,
      minValue: 0,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_PROPERTY_EXPENSES_FORM_SALES_TAX_INPUT,
    },
  },
  mhit: {
    fieldId: 'mhit',
    type: FieldType.INPUT,
    nameInModel: 'mhit',
    inputProps: {
      type: FieldInputType.CURRENCY,
      decimalScale: 2,
      placeholder: 'MHIT',
      label: 'MHIT (Manufactured Housing Inventory Tax)',
      disabled: false,
      required: false,
      minValue: 0,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_PROPERTY_EXPENSES_FORM_MHIT_INPUT,
    },
  },
};

export const getChattelPropertyExpensesConfig = compose(
  fillConfigFieldsWithKeys,
  ({ isMonthlyLotRentRequired, isLandContract = false }) => ({
    groups: [
      {
        type: FieldType.GROUP,
        // The chattel loan amount is derived from these: sales price + sales tax + MHIT - down payment.
        fields: [
          chattelPropertyExpensesConfigFields.salesPrice,
          chattelPropertyExpensesConfigFields.salesTax,
          chattelPropertyExpensesConfigFields.mhit,
        ],
        key: 'chattel-sales-contract',
        title: {
          label: 'Sales Contract',
          icon: InfoIcon,
          titleStyles: customTitleStyles,
          wrapperStyles: customTitleWrapperStyles,
          iconWrapperStyles: customIconWrapperStyles,
          iconStyles: customIconStyles,
        },
      },
      {
        type: FieldType.GROUP,
        fields: [
          {
            ...chattelPropertyExpensesConfigFields.monthlyLotRent,
            inputProps: {
              ...chattelPropertyExpensesConfigFields.monthlyLotRent.inputProps,
              required: isMonthlyLotRentRequired,
            },
          },
          // LEN-133: an owned / land-contract home carries a payment on the land itself.
          ...getConditionalArray(isLandContract, [
            chattelPropertyExpensesConfigFields.landMonthlyPayment,
          ]),
        ],
        key: 'chattel-property-expenses',
        title: {
          label: 'Property Expenses',
          icon: InfoIcon,
          titleStyles: customTitleStyles,
          wrapperStyles: customTitleWrapperStyles,
          iconWrapperStyles: customIconWrapperStyles,
          iconStyles: customIconStyles,
        },
      },
    ],
  }),
);
